import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PendingScrapeService } from '@/services/pending-scrape.service';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';

export const ApprovalQueue: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: pendingScrapes = [], isLoading } = useQuery({
    queryKey: ['pendingScrapes'],
    queryFn: () => PendingScrapeService.getPendingScrapes(),
  });

  const approveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!profile?.id) throw new Error('User not authenticated');
      return PendingScrapeService.approvePendingScrapes(ids, profile.id);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pendingScrapes'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      alert(`Approved ${result.success} leads. ${result.failed > 0 ? `${result.failed} failed.` : ''}`);
      setSelectedIds(new Set());
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      if (!profile?.id) throw new Error('User not authenticated');
      return PendingScrapeService.rejectPendingScrape(id, profile.id, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingScrapes'] });
    },
  });

  const handleSelectAll = () => {
    if (selectedIds.size === pendingScrapes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingScrapes.map(s => s.id)));
    }
  };

  const handleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleApproveSelected = () => {
    if (selectedIds.size === 0) {
      alert('Please select at least one item to approve');
      return;
    }
    if (!confirm(`Approve ${selectedIds.size} scraped restaurants and convert them to leads?`)) {
      return;
    }
    approveMutation.mutate(Array.from(selectedIds));
  };

  const handleApproveOne = (id: string) => {
    if (!confirm('Approve this restaurant and convert it to a lead?')) {
      return;
    }
    approveMutation.mutate([id]);
  };

  const handleRejectOne = (id: string) => {
    const reason = prompt('Rejection reason (optional):');
    rejectMutation.mutate({ id, reason: reason || undefined });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sales')}
          className="text-blue-600 hover:text-blue-700 text-sm mb-4"
        >
          ← Back to Sales
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Approval Queue</h1>
            <p className="text-gray-600 mt-1">
              {pendingScrapes.length} pending restaurant{pendingScrapes.length !== 1 ? 's' : ''} awaiting approval
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleApproveSelected}
              disabled={approveMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50"
            >
              Approve Selected ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      {pendingScrapes.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-600">No pending scrapes</p>
          <button
            onClick={() => navigate('/sales/scrape')}
            className="mt-4 inline-block text-blue-600 hover:text-blue-700"
          >
            Scrape some restaurants
          </button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === pendingScrapes.length && pendingScrapes.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employees</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scraped</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingScrapes.map((scrape) => {
                const data = scrape.scrapedData;
                return (
                  <tr key={scrape.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(scrape.id)}
                        onChange={() => handleSelect(scrape.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{data.businessName}</div>
                      {data.ownerName && (
                        <div className="text-sm text-gray-500">{data.ownerName}</div>
                      )}
                      {data.phone && (
                        <div className="text-sm text-gray-500">{data.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {data.address.city}, {data.address.state}
                      </div>
                      {data.address.zipCode && (
                        <div className="text-sm text-gray-500">{data.address.zipCode}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{data.restaurantType || 'N/A'}</div>
                      {data.priceTier && (
                        <div className="text-sm text-gray-500">{data.priceTier}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {data.estimatedEmployees || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{data.source}</div>
                      {data.sourceUrl && (
                        <a
                          href={data.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          View source
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(scrape.scrapedAt, 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleApproveOne(scrape.id)}
                        disabled={approveMutation.isPending || rejectMutation.isPending}
                        className="text-green-600 hover:text-green-700 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectOne(scrape.id)}
                        disabled={approveMutation.isPending || rejectMutation.isPending}
                        className="text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

